/**
 * Permission Actions
 * These constants represent the actions that can be performed on resources
 */
export const ACTIONS = {
  // Basic CRUD operations
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  
  // Special actions
  MANAGE: 'manage', // Full control (includes all CRUD operations)
  EXPORT: 'export',
  IMPORT: 'import',
  ASSIGN: 'assign',
  APPROVE: 'approve',
  REJECT: 'reject',
  PUBLISH: 'publish',
  ARCHIVE: 'archive',
};

/**
 * Permission Subjects
 * These constants represent the resources that actions can be performed on
 */
export const SUBJECTS = {
  // Special subjects
  ALL: 'all', // Represents all resources
  PUBLIC: 'public', // Publicly accessible resources
  
  TECHNOLOGY_RADAR: 'TechnologyRadar',
  TECHNOLOGY_RECONCILIATION: 'TechnologyReconciliation',
  // Application-specific resources
  PROJECT: 'Project',
  PROJECT_CODE: 'ProjectCode',
  PROJECT_NAME: 'ProjectName',
  SPECIAL_PROJECT: 'SpecialProject', // non delivery projects that holds employees: Bench, Default Internal.
  PROJECT_DASHBOARD: 'ProjectDashboard',
  PROJECT_RADAR: 'ProjectRadar',
  PROJECT_PURPOSE: 'ProjectPurpose',
  PROJECT_TYPE_POLICY: 'ProjectTypePolicy',
  PROJECT_REPOSITORY: 'ProjectRepository',
  // Employee-related resources
  EMPLOYEE: 'Employee',
  EMPLOYEE_RADAR: 'EmployeeRadar',
  //EMPLOYEES_RADAR: 'EmployeesRadar',
  INSTANT_FEEDBACK: 'InstantFeedback',
  EMPLOYEE_ASSIGNMENT: 'EmployeeAssignment',
  EMPLOYMENT_EPISODE: 'EmploymentEpisode',
  // ADR-019 — gates the employment episode/event edit modals (edit-only).
  // Granted as a single 'update' action; launch grant is admins only.
  EMPLOYMENT_EPISODE_MODIFICATION: 'EmploymentEpisodeModification',
  // ADR-168 D1 — held IN ADDITION TO EMPLOYMENT_EPISODE_MODIFICATION: correct a
  // CLOSED contract and the terminal events on it.
  EMPLOYMENT_EPISODE_HISTORY_CORRECTION: 'EmploymentEpisodeHistoryCorrection',
  // ADR-136 A5 — narrow episode annotation (comments + source contract link) on
  // episodes this system owns. Deliberately separate from
  // EMPLOYMENT_EPISODE_MODIFICATION, which also gates employment EVENT editing.
  EMPLOYMENT_EPISODE_ANNOTATION: 'EmploymentEpisodeAnnotation',
  // ADR-143 §9 — the episode-addressed contract amendment surface: create =
  // write an amendment, update = cancel a Pending one. Deliberately not
  // EMPLOYMENT_EVENT (the generic trigger's subject): the amendment
  // population is its own permission decision.
  CONTRACT_AMENDMENT: 'ContractAmendment',
  // ADR-176 — held IN ADDITION TO CONTRACT_AMENDMENT (update): revise a Pending
  // amendment in place (date, new values, reason). Seeded to admin only.
  CONTRACT_AMENDMENT_REVISION: 'ContractAmendmentRevision',
  // ADR-143 §12 — read-only: the change-record admin view + integrity
  // summary (records are written by server-side detectors only).
  EMPLOYMENT_CHANGE_RECORD: 'EmploymentChangeRecord',
  // ADR-164/165 — the employment data repairs and the overlapping position
  // legs report (manage). Admin only.
  EMPLOYMENT_DATA_REPAIR: 'EmploymentDataRepair',
  // ADR-166 — Employment Episode Discrepancies report (read). Admin at first.
  EMPLOYMENT_EPISODE_DISCREPANCY_REPORT: 'EmploymentEpisodeDiscrepancyReport',
  // ADR-019 — gates editing the hire date on the Employment Information tab
  // (disabled for everyone unless granted). Granted as a single 'update' action.
  EMPLOYEE_HIRE_DATE: 'EmployeeHireDate',
  // ADR-138 — gates the "take the hire date from an employment episode" action beside
  // the Hire Date field. Deliberately separate from EMPLOYEE_HIRE_DATE: that one
  // authorises ANY date and stays admin-only, while this one can only ever adopt the
  // start date of one of that employee's own episodes, so it is safe to grant wider.
  EMPLOYEE_HIRE_DATE_FROM_EPISODE: 'EmployeeHireDateFromEpisode',
  // ADR-171 C1 (ADR-144 §5, ADR-153 R4) — designate which open contract is the
  // person's primary (Contracts tab, "Make Primary").
  EMPLOYEE_PRIMARY_CONTRACT: 'EmployeePrimaryContract',

  EMPLOYEE_REPOSITORY: 'EmployeeRepository',
  DASHBOARD: 'Dashboard',
  SKILL: 'Skill',
  SKILLS_MATRIX: 'SkillsMatrix',
  FEEDBACK_SESSION: 'FeedbackSession',
  // ADR-003 phase 2 — gates the in-progress feedback-session modification UI
  // (assignee/reviewer action menus + Add Assignee button on the report page).
  // Granted as a single 'manage' action; replaces the phase-1 admin-only gate.
  FEEDBACK_SESSION_MODIFICATION: 'FeedbackSessionModification',
  // ADR-014 §9 — gates the snapshot close ("close with unfinished work") of a feedback session
  // that still has incomplete assignments. Irreversible; granted as a single
  // 'manage' action (Admin-only by default; widen via role grants).
  FEEDBACK_SESSION_CLOSURE: 'FeedbackSessionClosure',
  FEEDBACK_RESULT: 'FeedbackResult',
  FEEDBACK_SESSION_INSTANCE: 'FeedbackSessionInstance',
  FEEDBACK_SESSION_INSTANCE_OVERRIDE: 'FeedbackSessionInstanceOverride',
  FEEDBACK_SESSION_PROJECT: 'FeedbackSessionProject',
  // will be able to attach the final report to the employee assignment
  // using the Feedback on Demand feature
  FEEDBACK_REPORT_FINAL_ACTIONS: 'FeedbackReportFinalActions',

  EXTRA_EVALUATION: 'ExtraEvaluation',

  INDIVIDUAL_ASSIGNMENT: 'IndividualAssignment',

  IMPERSONATION_SESSION: 'ImpersonationSession',

  MY_CV: 'MyCV',
  CV: 'CV',
  CV_CONFIGURATION: 'CVConfiguration',

  CV_REPOSITORY: 'CVRepository',
  CV_EDUCATION_DEGREE: 'EducationDegree',
  CV_LANGUAGE: 'Language',
  CV_LANGUAGE_LEVEL: 'LanguageLevel',
  CV_CERTIFICATION_ISSUER: 'CertificationIssuer',
  CV_SKILLS_RECONCILIATION: 'SkillsReconciliation',
  CV_CERTIFICATION_ISSUER_RECONCILIATION: 'CertificationIssuerReconciliation',
  CV_CUSTOM_PROJECTS_RECONCILIATION: 'CustomProjectsReconciliation',
  CV_CUSTOM_CLASSIFICATIONS_RECONCILIATION: 'CustomClassificationsReconciliation',
  CV_CERTIFICATION_CATEGORY: 'CertificationCategory',
  CV_CERTIFICATION_NAME_RECONCILIATION: 'CertificationNameReconciliation',
  CERTIFICATIONS_REPORT: 'CertificationsReport',
  WORKFORCE_COMPOSITION_REPORT: 'WorkforceCompositionReport', // ADR-029
  DIRECT_MANAGER_DISCREPANCY: 'DirectManagerDiscrepancy',
  DIRECT_MANAGER_DISCREPANCY_RUN: 'DirectManagerDiscrepancyRun',
  DIRECT_MANAGER_DISCREPANCY_SOURCE_RECORD: 'DirectManagerDiscrepancySourceRecord', // ADR-131
  FEEDBACK_MODIFICATION_AUDIT_REPORT: 'FeedbackModificationAuditReport',


  ABSENCES: 'Absences',
  TIMESHEETS: 'Timesheets',
  INVOICES: 'Invoices',
  SOWS: 'Sows',
  ORDERS: 'Orders',
  BUSINESS_TRIPS: 'BusinessTrips',
  TERMS: 'Terms',
  CUSTOMER_ORDERS: 'CustomerOrders',


  REPORTS: 'Reports',
  ANALYTICS: 'Analytics',
  TAG: 'Tag',
  ORGANIZATION: 'Organization',
  ORGANIZATION_CONFIGURATION: 'OrganizationConfiguration',
  
  DATA_IMPORT: 'DataImport',
  PERMISSION: 'Permission',

  BUSINESS_UNIT: 'BusinessUnit',
  ORG_UNIT_TYPE: 'OrgUnitType',
  ORG_STRUCTURE_TYPE: 'OrgStructureType', // ADR-119
  //DEPARTMENT: 'Department',
  TEAM: 'Team',
  POSITION: 'Position',
  ROLE: 'Role',
  CONTRACT_TYPE: 'ContractType',
  ENGAGEMENT_TYPE: 'EngagementType',
  EVENT_TYPE: 'EventType',
  COUNTRY: 'Country',
  COMPANY: 'Company',
  CLIENT: 'Client',
  CLUSTER: 'Cluster',

  // Individual Development Plans
  IDP: 'IDP',
  IDP_REPOSITORY: 'IDPRepository',
  IDP_CONFIGURATION: 'IDPConfiguration',
  IDP_GOAL_STATUS: 'IDPGoalStatus',
  IDP_GOAL_STATUS_TRANSITION: 'IDPGoalStatusTransition',
  IDP_RAG_STATUS: 'IDPRAGStatus',
  IDP_MEASUREMENT_TYPE: 'IDPMeasurementType',
  IDP_RESOURCE_TEMPLATE: 'IDPResourceTemplate',
  MY_IDP: 'MyIDP',

  // Meetings (1:1 Check-ins)
  MEETING: 'Meeting',
  MEETING_REPOSITORY: 'MeetingRepository',
  MY_MEETING: 'MyMeeting',
  MEETING_CONFIGURATION: 'MeetingConfiguration',
  MEETING_TYPE: 'MeetingType',
  MEETING_ATTENDEE_TYPE: 'MeetingAttendeeType',
  MEETING_STATUS: 'MeetingStatus',
  MEETING_RELATION_TYPE: 'MeetingRelationType',
  MEETING_TALKING_POINT: 'MeetingTalkingPoint',

  MANAGER_HISTORY: 'ManagerHistory',

  // Bench module
  BENCH_EMPLOYEE_DATA: 'BenchEmployeeData',
  BENCH_SALARY: 'BenchSalary',
  BENCH_REPORT: 'BenchReport',

  // Bench candidates work list (ADR-107)
  BENCH_CANDIDATE: 'BenchCandidate',
  BENCH_CANDIDATE_SYNC_RUN: 'BenchCandidateSyncRun',
  BENCH_CANDIDATE_IMPORT: 'BenchCandidateImport',

  // Fixed Term Contracts Expiration report (ADR-090)
  FIXED_TERM_CONTRACT_REPORT: 'FixedTermContractReport',

  // Assignment Completion by Business Unit report (ADR-093)
  ASSIGNMENT_COMPLETION_REPORT: 'AssignmentCompletionReport',

  // Per-delivery-center expectations feeding that report (ADR-095)
  ASSIGNMENT_COMPLETION_POLICY: 'AssignmentCompletionPolicy',

  // Ingestion configuration: job position mappings used by the Americas and
  // PowerBI syncs (ADR-132). read = view, manage = edit / import.
  INGESTION_CONFIGURATION: 'IngestionConfiguration',
  // Reports → Ingestion Sync Runs (ADR-133): list/detail (read), Trigger now (create)
  INGESTION_SYNC_RUN: 'IngestionSyncRun',
  // The full stored record of one result item (ADR-133 D10)
  INGESTION_SYNC_RUN_RECORD: 'IngestionSyncRunRecord',
  // Reports → Scheduled Jobs (ADR-198): read-only status of radar-scheduler jobs
  SCHEDULER_STATUS: 'SchedulerStatus',

  // Organization Hierarchy report (ADR-096) — dedicated ability, deliberately
  // separate from the broad ORGANIZATION subject that gates the CRUD screens
  ORGANIZATION_HIERARCHY_REPORT: 'OrganizationHierarchyReport',

  // Management info history on the employee profile (ADR-101) — dedicated
  // ability, separate from broad EMPLOYEE access
  MANAGEMENT_HISTORY: 'ManagementHistory',

  // Placement Activity module
  PLACEMENT_ACTIVITY: 'PlacementActivity',
  PLACEMENT_ACTIVITY_BENCH: 'PlacementActivityBench',  // ADR-009 — cross-BU bench-window edit
  PLACEMENT_ACTIVITY_TYPE: 'PlacementActivityType',
};
